import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useAppConfig } from '../hooks/useAppConfig';
import { useLanguages } from '../hooks/useLanguages';
import appLogo from '../assets/branding/logo.png';
import LanguageFlag from './LanguageFlag';
import WalkthroughOverlay from './WalkthroughOverlay';
import { hasSeenWalkthrough, markWalkthroughSeen } from '../services/walkthrough';

function getInitials(user) {
  const label = (user?.name || user?.email || '?').trim();
  const words = label.split(/\s+/);
  return (words.length >= 2 ? words[0][0] + words[1][0] : label.slice(0, 2)).toUpperCase();
}

export default function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout, languagePrefs, openLanguagePreferences } = useAuth();
  const { languages } = useLanguages();
  const { t } = useTranslation();
  const config = useAppConfig(languagePrefs?.nativeLanguage);

  // First-run spotlight tour — mirrors the mobile app's WalkthroughOverlay,
  // pointed at these two nav controls instead of the lobby screen's pills
  // (the closest web equivalent, always mounted regardless of route). Both
  // live in the desktop-only "xl:flex" control group, so on narrow
  // viewports WalkthroughOverlay's own visibility check skips them and the
  // tour ends silently rather than pointing at a hidden element.
  const languagePillRef = useRef(null);
  const levelSelectRef = useRef(null);
  const [showWalkthrough, setShowWalkthrough] = useState(false);

  useEffect(() => {
    let active = true;
    hasSeenWalkthrough().then((seen) => {
      if (active && !seen) setShowWalkthrough(true);
    });
    return () => { active = false; };
  }, []);

  function finishWalkthrough(status) {
    markWalkthroughSeen(status);
    setShowWalkthrough(false);
  }

  const langMap = useMemo(() => Object.fromEntries(languages.map((l) => [l.code, l])), [languages]);
  const nativeLang = languagePrefs ? langMap[languagePrefs.nativeLanguage] : null;
  const targetLang = languagePrefs ? langMap[languagePrefs.targetLanguage] : null;
  const courseLevels = Array.isArray(config.level)
    ? config.level
      .map((level) => String(level || '').trim().toLowerCase())
      .filter(Boolean)
    : [];
  const requestedCourseLevel = location.pathname.match(/^\/(?:curriculum|topic-lessons)\/([^/]+)/)?.[1]?.toLowerCase()
    || new URLSearchParams(location.search).get('level')?.toLowerCase()
    || '';
  const selectedCourseLevel = courseLevels.includes(requestedCourseLevel)
    ? requestedCourseLevel
    : courseLevels.includes('a1')
      ? 'a1'
      : courseLevels[0] || '';

  useEffect(() => setIsMenuOpen(false), [location.pathname]);

  const isActive = (path) => location.pathname === path;
  const inSkillPath = location.pathname === '/skill-path';
  const inTopicLessons = location.pathname.startsWith('/topic-lessons/');
  const inStaticPages = location.pathname === '/quick-sheet';
  const inPhrasebook = location.pathname.startsWith('/phrasebook');
  const inTopWords = location.pathname === '/top-words';
  const inAudioLectures = location.pathname === '/audio-lectures';

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const selectCourseLevel = (event) => {
    if (!event.target.value) return;
    if (inTopicLessons) {
      navigate(`/topic-lessons/${event.target.value}`);
      return;
    }
    navigate(`/?level=${event.target.value}`);
  };

  return (
    <>
    <div className="w-full bg-amber-50 border-b border-amber-200 px-4 py-1.5 text-center text-xs text-amber-800">
      {t('disclaimer')}
    </div>
    <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/92 backdrop-blur-lg">
      <nav className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">

        {/* ── Brand + primary links ── */}
        <div className="flex items-center gap-4">
          <Link to="/" className="group flex min-w-0 items-center gap-2.5">
            <img
              src={appLogo}
              alt="SublingoX"
              className="h-9 w-9 rounded-xl object-cover ring-2 ring-indigo-100 transition group-hover:ring-indigo-300"
            />
            <span className="truncate text-base font-black text-slate-900 sm:text-lg">
              <span className="bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
                SublingoX
              </span>
            </span>
          </Link>

          {/* Desktop links */}
          <div className="hidden items-center gap-1 xl:flex">
            <Link
              to="/"
              className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
                isActive('/') ? 'text-indigo-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              {t('nav.home')}
              {isActive('/') && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-indigo-600" />}
            </Link>

            <Link
              to="/skill-path"
              className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
                inSkillPath ? 'text-indigo-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              {t('learningPaths.skillTitle')}
              {inSkillPath && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-indigo-600" />}
            </Link>

            <Link
              to={`/topic-lessons/${selectedCourseLevel || 'a1'}`}
              className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
                inTopicLessons ? 'text-indigo-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              {t('learningPaths.topicTitle')}
              {inTopicLessons && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-indigo-600" />}
            </Link>

            <Link
              to="/exercises"
              className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
                isActive('/exercises') ? 'text-indigo-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              {t('exercise.dailyTests')}
              {isActive('/exercises') && (
                <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-indigo-600" />
              )}
            </Link>

            <Link
              to="/games"
              className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
                isActive('/games') ? 'text-indigo-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              {t('games.title')}
              {isActive('/games') && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-indigo-600" />}
            </Link>

            <Link
              to="/download"
              className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
                isActive('/download') ? 'text-indigo-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              {t('nav.downloadApp')}
              {isActive('/download') && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-indigo-600" />}
            </Link>

            <Link
              to="/quick-sheet"
              className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
                inStaticPages ? 'text-indigo-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              {t('nav.quickSheet')}
              {inStaticPages && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-indigo-600" />}
            </Link>

            <Link
              to="/phrasebook"
              className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
                inPhrasebook ? 'text-indigo-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              {t('nav.phrasebook')}
              {inPhrasebook && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-indigo-600" />}
            </Link>

            <Link
              to="/top-words"
              className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
                inTopWords ? 'text-indigo-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              {t('nav.topWords')}
              {inTopWords && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-indigo-600" />}
            </Link>

            <Link
              to="/audio-lectures"
              className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
                inAudioLectures ? 'text-indigo-700' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
              }`}
            >
              {t('nav.audioLectures')}
              {inAudioLectures && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-indigo-600" />}
            </Link>
          </div>
        </div>

        {/* ── Auth controls ── */}
        <div className="hidden items-center gap-2 xl:flex">
          {/* Language pair pill */}
          {nativeLang && targetLang ? (
            <button
              ref={languagePillRef}
              onClick={openLanguagePreferences}
              title={t('nav.setLanguages')}
              className="flex items-center gap-1.5 rounded-xl border border-indigo-100 bg-indigo-50 px-3 py-1.5 text-xs font-bold text-indigo-700 transition hover:border-indigo-300 hover:bg-indigo-100"
            >
              <span className="flex items-center gap-1.5"><LanguageFlag flag={nativeLang.flag} label={nativeLang.label} />{nativeLang.label}</span>
              <svg className="h-3 w-3 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
              <span className="flex items-center gap-1.5"><LanguageFlag flag={targetLang.flag} label={targetLang.label} />{targetLang.label}</span>
            </button>
          ) : (
            <button
              ref={languagePillRef}
              onClick={openLanguagePreferences}
              title={t('nav.setLanguages')}
              className="flex items-center gap-1.5 rounded-xl border border-indigo-100 bg-indigo-50 px-3 py-1.5 text-xs font-bold text-indigo-700 transition hover:border-indigo-300 hover:bg-indigo-100"
            >
              <span>🌍</span>
              <span>{t('nav.setLanguages')}</span>
            </button>
          )}

          {courseLevels.length > 0 && (
            <div ref={levelSelectRef} className="relative">
              <label className="sr-only" htmlFor="header-course-level">Choose a course level</label>
              <select
                id="header-course-level"
                value={courseLevels.includes(selectedCourseLevel) ? selectedCourseLevel : ''}
                onChange={selectCourseLevel}
                className="appearance-none rounded-xl border border-violet-200 bg-violet-50 py-1.5 pl-3 pr-7 text-xs font-black uppercase text-violet-700 transition hover:border-violet-300 hover:bg-violet-100 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
              >
                {!selectedCourseLevel && <option value="">Level</option>}
                {courseLevels.map((level) => <option key={level} value={level}>{level.toUpperCase()}</option>)}
              </select>
              <svg className="pointer-events-none absolute right-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
              </svg>
            </div>
          )}

          {isAuthenticated ? (
            <>
              <button
                onClick={() => navigate('/profile')}
                title={user?.name || user?.email}
                aria-label={t('profile.title')}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-xs font-black text-white shadow-md transition hover:shadow-lg hover:scale-105 select-none"
              >
                {getInitials(user)}
              </button>
              <button
                onClick={handleLogout}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
              >
                {t('nav.logout')}
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="rounded-xl px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-100">
                {t('nav.login')}
              </Link>
              <Link
                to="/register"
                className="rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2 text-sm font-bold text-white shadow-md shadow-indigo-600/20 transition hover:-translate-y-px hover:shadow-indigo-600/40"
              >
                {t('nav.getStarted')}
              </Link>
            </>
          )}
        </div>

        {/* ── Mobile menu toggle ── */}
        <button
          type="button"
          onClick={() => setIsMenuOpen((v) => !v)}
          className="rounded-lg p-2 text-slate-600 transition hover:bg-slate-100 xl:hidden"
          aria-label="Toggle menu"
          aria-expanded={isMenuOpen}
          aria-controls="mobile-navigation-menu"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            {isMenuOpen
              ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              : <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            }
          </svg>
        </button>
      </nav>

      {/* ── Mobile drawer ── */}
      {isMenuOpen && (
        <div
          id="mobile-navigation-menu"
          className="max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain border-t border-slate-100 bg-white/98 backdrop-blur-lg xl:hidden"
        >
          <div
            className="space-y-1 px-4 pt-4"
            style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
          >
            {/* Mobile language pair pill */}
            {nativeLang && targetLang ? (
              <button
                type="button"
                onClick={openLanguagePreferences}
                className="mb-2 flex w-full min-w-0 items-center justify-between gap-2 rounded-xl border border-indigo-100 bg-indigo-50 px-3 py-2 text-xs font-bold text-indigo-700"
              >
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="flex min-w-0 items-center gap-1.5"><LanguageFlag flag={nativeLang.flag} label={nativeLang.label} /><span className="truncate">{nativeLang.label}</span></span>
                  <svg className="h-3 w-3 shrink-0 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                  <span className="flex min-w-0 items-center gap-1.5"><LanguageFlag flag={targetLang.flag} label={targetLang.label} /><span className="truncate">{targetLang.label}</span></span>
                </span>
                <span className="shrink-0 text-[10px] font-semibold text-indigo-400">{t('nav.tapToChange')}</span>
              </button>
            ) : (
              <button
                type="button"
                onClick={openLanguagePreferences}
                className="mb-2 flex w-full min-w-0 items-center justify-between gap-2 rounded-xl border border-indigo-100 bg-indigo-50 px-3 py-2 text-xs font-bold text-indigo-700"
              >
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="shrink-0">🌍</span>
                  <span className="truncate">{t('nav.setLanguages')}</span>
                </span>
                <span className="shrink-0 text-[10px] font-semibold text-indigo-400">{t('nav.tapToChoose')}</span>
              </button>
            )}

            {courseLevels.length > 0 && (
              <div className="mb-3 relative">
                <label className="sr-only" htmlFor="mobile-course-level">Choose a course level</label>
                <select
                  id="mobile-course-level"
                  value={courseLevels.includes(selectedCourseLevel) ? selectedCourseLevel : ''}
                  onChange={selectCourseLevel}
                  className="w-full appearance-none rounded-xl border border-violet-200 bg-violet-50 px-3 py-2.5 text-sm font-black uppercase text-violet-700 focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
                >
                  {!selectedCourseLevel && <option value="">Choose course level</option>}
                  {courseLevels.map((level) => <option key={level} value={level}>{level.toUpperCase()}</option>)}
                </select>
                <svg className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
                </svg>
              </div>
            )}

            {/* Mobile brand row */}
            <div className="mb-3 flex items-center gap-2.5 rounded-xl bg-gradient-to-r from-indigo-50 to-violet-50 p-2.5">
              <img src={appLogo} alt="SublingoX" className="h-9 w-9 rounded-lg object-cover ring-1 ring-indigo-200" />
              <div>
                <p className="text-sm font-black text-slate-900">SublingoX</p>
                <p className="text-xs text-slate-500">{t('nav.languagePlatform')}</p>
              </div>
            </div>

            <Link
              to="/"
              className={`block rounded-xl px-3 py-2.5 text-sm font-bold transition ${isActive('/') ? 'bg-indigo-50 text-indigo-700' : 'text-slate-700 hover:bg-slate-50'}`}
            >
              {t('nav.home')}
            </Link>

            <Link
              to="/skill-path"
              className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-bold transition ${
                inSkillPath ? 'bg-indigo-50 text-indigo-700' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span aria-hidden="true">🪜</span>
              <span>{t('learningPaths.skillTitle')}</span>
            </Link>

            <Link
              to={`/topic-lessons/${selectedCourseLevel || 'a1'}`}
              className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-bold transition ${
                inTopicLessons ? 'bg-indigo-50 text-indigo-700' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span aria-hidden="true">📚</span>
              <span>{t('learningPaths.topicTitle')}</span>
            </Link>

            <Link
              to="/exercises"
              className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-bold transition ${
                isActive('/exercises') ? 'bg-indigo-50 text-indigo-700' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span>✅</span>
              <span>{t('exercise.dailyTests')}</span>
            </Link>

            <Link
              to="/games"
              className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-bold transition ${
                isActive('/games') ? 'bg-indigo-50 text-indigo-700' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span>🎮</span>
              <span>{t('games.title')}</span>
            </Link>

            <Link
              to="/download"
              className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-bold transition ${
                isActive('/download') ? 'bg-indigo-50 text-indigo-700' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              <span>📱</span>
              <span>{t('nav.downloadApp')}</span>
            </Link>

            <Link
              to="/quick-sheet"
              className={`mt-2 flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${isActive('/quick-sheet') ? 'bg-indigo-50 text-indigo-700' : 'text-slate-700 hover:bg-slate-50'}`}
            >
              <span aria-hidden="true">▤</span>
              <span>{t('nav.quickSheet')}</span>
            </Link>

            <Link
              to="/phrasebook"
              className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${inPhrasebook ? 'bg-indigo-50 text-indigo-700' : 'text-slate-700 hover:bg-slate-50'}`}
            >
              <span aria-hidden="true">🌐</span>
              <span>{t('nav.phrasebook')}</span>
            </Link>

            <Link
              to="/top-words"
              className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${inTopWords ? 'bg-indigo-50 text-indigo-700' : 'text-slate-700 hover:bg-slate-50'}`}
            >
              <span aria-hidden="true">🔤</span>
              <span>{t('nav.topWords')}</span>
            </Link>

            <Link
              to="/audio-lectures"
              className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold transition ${inAudioLectures ? 'bg-indigo-50 text-indigo-700' : 'text-slate-700 hover:bg-slate-50'}`}
            >
              <span aria-hidden="true">🎧</span>
              <span>{t('nav.audioLectures')}</span>
            </Link>

            <div className="mt-3 border-t border-slate-100 pt-3">
              {isAuthenticated ? (
                <>
                  <div className="mb-2 flex items-center gap-2.5 px-3">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-[10px] font-black text-white select-none">
                      {getInitials(user)}
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold text-slate-800">
                        {user?.isAnonymous ? t('profile.guest') : (user?.name || t('profile.learner'))}
                      </p>
                      {user?.email && (
                        <p className="truncate text-xs text-slate-500">{user.email}</p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => navigate('/profile')}
                    className="block w-full rounded-xl px-3 py-2.5 text-left text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    {t('nav.viewProfile')}
                  </button>
                  <button
                    onClick={handleLogout}
                    className="block w-full rounded-xl px-3 py-2.5 text-left text-sm font-bold text-slate-700 hover:bg-slate-50"
                  >
                    {t('nav.logout')}
                  </button>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Link to="/login" className="rounded-xl border border-slate-200 py-2.5 text-center text-sm font-bold text-slate-700 hover:bg-slate-50">
                    {t('nav.login')}
                  </Link>
                  <Link to="/register" className="rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 py-2.5 text-center text-sm font-bold text-white">
                    {t('nav.getStarted')}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
    <WalkthroughOverlay
      active={showWalkthrough}
      steps={[
        { target: languagePillRef, title: t('walkthrough.langTitle'), description: t('walkthrough.langDesc') },
        { target: levelSelectRef, title: t('walkthrough.levelTitle'), description: t('walkthrough.levelDesc') },
      ]}
      onFinished={finishWalkthrough}
    />
    </>
  );
}
